import { TableViewStore, BaseListViewStore } from "core/stores";
import { PickTicketDetailService, PickTicketHeaderService } from "application/pick-ticket/services";
import { ScheduleService } from 'application/schedule/services';
import { action, observable } from "mobx";
import { GridBooleanCell } from "core/components";
import { resolveColumns } from "application/facility/util/resolveColumns";
import { ModalParams } from "core/models";

class PickTicketDetailListViewStore extends BaseListViewStore {
    @observable accessor pickTicketHeader = null;
    @observable accessor deliveryDate = null;
    @observable accessor initPageLoad = true;

    constructor(rootStore) {
        const pickTicketDetailService = new PickTicketDetailService(rootStore.application.baasic.apiClient);
        const pickTicketHeaderService = new PickTicketHeaderService(rootStore.application.baasic.apiClient);
        const scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);

        const pickTicketHeaderId = rootStore.routerStore.routerState.params.pickTicketHeaderId;

        super(rootStore, {
            autoInit: true,
            name: 'pick-ticket-detail-list',
            routes: {},
            actions: () => {
                return {
                    find: async params => {
                        if (this.initPageLoad) {
                            await this.getPickTicketHeader(pickTicketHeaderId);
                            this.initPageLoad = false;
                        }
                        const response = await pickTicketDetailService.find({
                            ...params,
                            pickTicketHeaderId,
                            embed: 'Product'
                        });
                        const responseData = response.data.item.map(obj => { return { ...obj, uom: obj.product && obj.product.unitOfMeasure && obj.product.unitOfMeasure.abrv } });
                        return responseData;
                    }
                };
            },
        });

        this.pickTicketDetailService = pickTicketDetailService;
        this.pickTicketHeaderService = pickTicketHeaderService;
        this.scheduleService = scheduleService;

        this.pickTicketHeaderId = pickTicketHeaderId;

        this.backOrderEditModal = new ModalParams({});
        this.pickTicketInfoModal = new ModalParams({});

        this.setTableStore(
            new TableViewStore(this.queryUtility, {
                columns: resolveColumns(
                    [
                        {
                            key: 'itemId',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.ITEM_ID"
                        },
                        {
                            key: 'itemName',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.ITEM_NAME",
                            width: 400
                        },
                        {
                            key: 'category',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.CATEGORY"
                        },
                        {
                            key: 'uom',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.UOM"
                        },
                        {
                            key: 'qty',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.QTY",
                            width: 100
                        },
                        {
                            key: 'backOrder',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.BACK_ORDER",
                            cell: GridBooleanCell,
                            displayRedIcon: true,
                            width: 110,
                            headerClassName: "center"
                        },
                        {
                            key: 'backOrderNote',
                            title: "PICK_TICKET_DETAIL.LIST.COLUMNS.BACK_ORDER_NOTE",
                        }
                    ]
                ),
                actions: {
                    onEdit: (item) => { this.openBackOrderEditModal(item.id) }
                }
            })
        );
    }

    @action.bound
    async sendOrder() {
        try {
            await this.scheduleService.sendOrder({ pickTicketHeaderId: this.pickTicketHeaderId });
            this.rootStore.notificationStore.success("Facility Schedule Date successfully updated.");
        } catch (err) {
            this.rootStore.notificationStore.error("An unexpected error occurred while trying to send order", err);
        }
    }

    @action.bound
    async refresh() {
        const { modalStore, notificationStore } = this.rootStore;
        modalStore.showConfirm(
            'PICK_TICKET_DETAIL.REFRESH.CONFIRM_REFRESH',
            async () => {
                try {
                    await this.pickTicketHeaderService.refresh(this.pickTicketHeader.id);
                    notificationStore.success('PICK_TICKET_DETAIL.REFRESH.SUCCESS_REFRESH');
                    await this.queryUtility.fetch();
                } catch (err) {
                    notificationStore.error('PICK_TICKET_DETAIL.REFRESH.ERROR_REFRESH', err);
                }
            }
        );
    }

    async getPickTicketHeader(pickTicketHeaderId) {
        const response = await this.pickTicketHeaderService.get(pickTicketHeaderId, {
            embed: "facilityOrders,pickTicketHeaderStatus"
        });

        this.pickTicketHeader = response.data;
        const facilityName = this.pickTicketHeader && this.pickTicketHeader.facilityOrders[0].facility.name;
        this.rootStore.titleStore.setTitle(facilityName ? `PT - ${facilityName}` : 'Pick Ticket');

        this.facilityOrderId = response.data && response.data.facilityOrders[0].id;
        await this.getDeliveryDate(response.data && response.data.facilityOrders[0].id)

        if (localStorage.getItem('refreshPickTicket')) {
            localStorage.removeItem('refreshPickTicket');
            try {
                if (this.pickTicketHeader.pickTicketHeaderStatus && this.pickTicketHeader.pickTicketHeaderStatus.abrv === 'new')
                    await this.pickTicketHeaderService.refresh(this.pickTicketHeader.id);
            } catch (err) {
                this.rootStore.notificationStore.error(err && err.data ? err.data : 'PICK_TICKET_DETAIL.REFRESH.ERROR_REFRESH');
            }
        }

        const includedExcludedItems = (await this.pickTicketHeaderService.getExcludedAndIncludedFacilityOrderTemplateItems(this.facilityOrderId)).data.item;
        if(includedExcludedItems.some(i => i))
            this.openPickTicketInfoModal();
    }

    @action.bound
    async getDeliveryDate(facilityOrderId) {
        const typeResponse = await this.scheduleService.getTypes();
        let orderType = typeResponse.item.find(i => i.abrv = 'delivery');
        const response = await this.scheduleService.find({ facilityOrderId: facilityOrderId, calendarTypeId: orderType && orderType.id });
        this.deliveryDate = response.data && response.data.item[0] && response.data.item[0].date;
    }

    openBackOrderEditModal(pickTicketDetailId) {
        this.backOrderEditModal.open({
            pickTicketDetailId,
            fetchTable: this.queryUtility.fetch
        });
    }

    @action.bound
    async downloadPickTicket(format) {
        const pickTicketReportDownloadUrl = `${this.pickTicketHeaderService.getPickTicketReportDownloadUrl(this.pickTicketHeaderId)}?format=${format}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl}&ttype=${this.rootStore.authStore.token.type}`;
        window.open(pickTicketReportDownloadUrl);
    }

    @action.bound
    async openPickTicketInfoModal() {
        this.pickTicketInfoModal.open({
            facilityOrderId: this.facilityOrderId
        });
    }
}

export default PickTicketDetailListViewStore;